// components/modals/AddMultipleTasksModal.tsx - Modal for creating multiple tasks from a project
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { taskService, CreateTaskData } from '../../services/taskApi.ts';

// Canonical ProjectTask interface
interface ProjectTask {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  category: 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other';
  estimatedTime: '0.5h' | '1h' | '2h' | '4h' | '8h';
  tags: string;
  dueDate: string;
  status: 'Todo' | 'InProgress' | 'Done';
}

interface ProjectData {
  id: string;
  name: string;
  description: string;
  category: string;
  tasks: ProjectTask[]; // Canonical Task model
  sourceIdeaId?: string;
}

interface TaskFormData {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  category: 'App Idea' | 'Web App' | 'Social App' | 'EdTech' | 'Business Idea' | 'Product Concept' | 'Feature Request' | 'Research Topic' | 'Creative Project' | 'Other';
  estimatedTime: '0.5h' | '1h' | '2h' | '4h' | '8h';
  tags: string;
  dueDate: string;
  status: 'Todo' | 'InProgress' | 'Done';
}

interface AddMultipleTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTasksAdded: (tasks: any[]) => void;
  projectData?: ProjectData | null;
}

const AddMultipleTasksModal: React.FC<AddMultipleTasksModalProps> = ({
  isOpen,
  onClose,
  onTasksAdded,
  projectData
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [tasksData, setTasksData] = useState<TaskFormData[]>([]);

  // Initialize tasks data from project when modal opens
  useEffect(() => {
    if (isOpen && projectData?.tasks && projectData.tasks.length > 0) {
      const initialTasks = (projectData.tasks as ProjectTask[]).map((task, idx) => ({
        title: task.title || task.name || '',
        description: task.description,
        priority: task.priority,
        category: task.category,
        estimatedTime: task.estimatedTime,
        tags: task.tags,
        dueDate: task.dueDate || projectData.dueDate || '',
        status: task.status
      }));
      setTasksData(initialTasks);
      setCurrentTaskIndex(0);
      setError(null);
    } else if (isOpen && (!projectData?.tasks || projectData.tasks.length === 0)) {
      setTasksData([{
        title: projectData?.title || '',
        description: projectData?.description || '',
        priority: 'Medium',
        category: projectData?.category || 'App Idea',
        estimatedTime: '1h',
        tags: '',
        dueDate: projectData?.dueDate || '',
        status: 'Todo'
      }]);
      setCurrentTaskIndex(0);
      setError(null);
    }
  }, [isOpen, projectData]);

  const updateCurrentTask = (field: keyof TaskFormData, value: string) => {
    setTasksData(prev => prev.map((task, index) => 
      index === currentTaskIndex 
        ? { ...task, [field]: value }
        : task
    ));
  };

  const addNewTask = () => {
    const newTask: TaskFormData = {
      title: '',
      description: '',
      priority: 'Medium',
      category: 'App Idea',
      estimatedTime: '1h',
      tags: '',
      dueDate: '',
      status: 'Todo'
    };
    setTasksData(prev => [...prev, newTask]);
    setCurrentTaskIndex(tasksData.length);
  };

  const removeTask = (index: number) => {
    if (tasksData.length <= 1) return; // Don't remove if it's the last task
    
    setTasksData(prev => prev.filter((_, i) => i !== index));
    if (currentTaskIndex >= tasksData.length - 1) {
      setCurrentTaskIndex(Math.max(0, currentTaskIndex - 1));
    }
  };

  const navigateTask = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    } else if (direction === 'next' && currentTaskIndex < tasksData.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const handleSubmit = async () => {
    // Validate all tasks
  const validTasks = tasksData.filter(task => typeof task.title === 'string' && task.title.trim());
    if (validTasks.length === 0) {
      setError('At least one task must have a title');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const createdTasks: any[] = [];
      
      for (const taskData of validTasks) {
        const createData: CreateTaskData = {
          title: typeof taskData.title === 'string' ? taskData.title.trim() : '',
          description: taskData.description || '',
          priority: taskData.priority,
          category: taskData.category,
          estimatedTime: taskData.estimatedTime,
          tags: taskData.tags || '',
          dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : undefined,
          sourceProjectId: projectData?.id || undefined,
          sourceIdeaId: projectData?.sourceIdeaId || undefined
        };

        const createdTask = await taskService.createTask(createData);
        createdTasks.push(createdTask);
      }
      
      // Call the callback with all created tasks
      onTasksAdded(createdTasks);
      
      // Close modal
      onClose();
    } catch (err) {
      console.error('Error creating tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to create tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentTask = tasksData[currentTaskIndex];
  if (!currentTask) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Create Tasks {projectData ? `from ${projectData.name}` : ''}
            </h3>
            <p className="text-sm text-gray-600">
              Task {currentTaskIndex + 1} of {tasksData.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              type="text"
              value={currentTask.title}
              onChange={(e) => updateCurrentTask('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter task title..."
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={currentTask.description}
              onChange={(e) => updateCurrentTask('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Enter task description..."
              disabled={loading}
            />
          </div>

          {/* Priority and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={currentTask.priority}
                onChange={(e) => updateCurrentTask('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={currentTask.category}
                onChange={(e) => updateCurrentTask('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="App Idea">App Idea</option>
                <option value="Web App">Web App</option>
                <option value="Social App">Social App</option>
                <option value="EdTech">EdTech</option>
                <option value="Business Idea">Business Idea</option>
                <option value="Product Concept">Product Concept</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Research Topic">Research Topic</option>
                <option value="Creative Project">Creative Project</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Due Date and Estimated Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={currentTask.dueDate}
                onChange={(e) => updateCurrentTask('dueDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Time
              </label>
              <select
                value={currentTask.estimatedTime}
                onChange={(e) => updateCurrentTask('estimatedTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="0.5h">30 minutes</option>
                <option value="1h">1 hour</option>
                <option value="2h">2 hours</option>
                <option value="4h">4 hours</option>
                <option value="8h">8 hours</option>
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={currentTask.status}
              onChange={(e) => updateCurrentTask('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="Todo">📋 Todo</option>
              <option value="InProgress">⏳ In Progress</option>
              <option value="Done">✅ Done</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={currentTask.tags}
              onChange={(e) => updateCurrentTask('tags', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Frontend, React, Urgent"
              disabled={loading}
            />
          </div>
        </div>

        {/* Navigation and Task Management */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateTask('prev')}
              disabled={currentTaskIndex === 0 || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-gray-600 min-w-[100px] text-center">
              {currentTaskIndex + 1} of {tasksData.length}
            </span>
            <button
              onClick={() => navigateTask('next')}
              disabled={currentTaskIndex === tasksData.length - 1 || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={addNewTask}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 btn-primary"
            >
              <Plus size={16} />
              <span>Add Task</span>
            </button>
            {tasksData.length > 1 && (
              <button
                onClick={() => removeTask(currentTaskIndex)}
                disabled={loading}
                className="flex items-center space-x-1 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 size={16} />
                <span>Remove</span>
              </button>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 btn-primary"
            disabled={loading || tasksData.every(task => typeof task.title !== 'string' || !task.title.trim())}
          >
            {loading ? 'Creating...' : `Create ${tasksData.filter(task => typeof task.title === 'string' && task.title.trim()).length} Tasks`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMultipleTasksModal;
